import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  loadAuthFromStorage,
  selectCurrentToken,
  selectAuthLoading,
  selectIsAuthenticated,
} from "./redux/redux/auth"; // Adjust path to authSlice
import Header from "./components/navbar/Header";
import AdminLogin from "./pages/AdminLogin";
import AdminHeader from "./components/navbar/AdminHeader";
import NotFound from "./pages/pageNotFound";
import UnAuthenticatedHeader from "@/components/Header";

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-gray-100 bg-opacity-75 z-50">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
  </div>
);

// Public Route Component for routes like login
const PublicRoute = ({ children }) => {
  const token = useSelector(selectCurrentToken);
  const isLoading = useSelector(selectAuthLoading);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadAuthFromStorage());
  }, [dispatch]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Redirect to dashboard if authenticated
  if (token) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Protected Route Component for regular users
const ProtectedRoute = ({ children }) => {
  const token = useSelector(selectCurrentToken);
  const isLoading = useSelector(selectAuthLoading);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadAuthFromStorage());
  }, [dispatch]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!token) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Protected Route Component for admins
const AdminProtectedRoute = ({ children }) => {
  const isAdmin = localStorage.getItem("isAdmin") === "true";

  if (!isAdmin) {
    return <Navigate to="/admin-login" replace />;
  }

  return children;
};

// Lazy-loaded components

const Login = lazy(() => import("./pages/login"));
const Register = lazy(() => import("./pages/register"));
const Pricing = lazy(() => import("./pages/pricing"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Dashboard = lazy(() => import("./pages/authenticated_page/Dashboard"));
const Fastag = lazy(() => import("./pages/authenticated_page/Fastag"));
const Vahan = lazy(() => import("./pages/authenticated_page/vahan"));
const AdminVahan = lazy(() => import("./pages/admin/vahan"));
const AdminFastag = lazy(() => import("./pages/admin/Fastag"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));

const Sarathi = lazy(() => import("./pages/authenticated_page/sarathi"));
const VehicleOwner = lazy(() =>
  import("./pages/authenticated_page/VehicleOwner")
);
const MyVehicles = lazy(() => import("./pages/authenticated_page/MyVehicles"));
const Searchhistory = lazy(() =>
  import("./pages/authenticated_page/Searchhistory")
);
const Transaction = lazy(() =>
  import("./pages/authenticated_page/Transaction")
);
const Vehicle = lazy(() => import("./pages/admin/Vehicle"));
const Order = lazy(() => import("./pages/admin/Order"));
const Verification = lazy(() => import("./pages/admin/Verification"));
const Company = lazy(() => import("./pages/admin/company"));
const AdminDriver = lazy(() => import("./pages/admin/Driver"));
const ApitHit = lazy(() => import("./pages/admin/ApitHit"));
const App = () => {
  const isLoading = useSelector(selectAuthLoading);
  const isAuth = useSelector(selectIsAuthenticated);

  console.log("isAuth:", isAuth);

  return (
    <div className="min-h-screen bg-white">
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route
            path="/"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Header />
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/fastag"
            element={
              <ProtectedRoute>
                <Header />
                <Fastag />
              </ProtectedRoute>
            }
          />
          <Route
            path="/searchhistory"
            element={
              <ProtectedRoute>
                <Header />
                <Searchhistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/transaction"
            element={
              <ProtectedRoute>
                <Header />
                <Transaction />
              </ProtectedRoute>
            }
          />
          <Route
            path="/MyVehicles"
            element={
              <ProtectedRoute>
                <Header />
                <MyVehicles />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vahan"
            element={
              <ProtectedRoute>
                <Header />
                <Vahan />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sarathi"
            element={
              <ProtectedRoute>
                <Header />
                <Sarathi />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vehicle-owner"
            element={
              <ProtectedRoute>
                <Header />
                <VehicleOwner />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pricing"
            element={
              <>
                {isAuth && !isLoading ? <Header /> : <UnAuthenticatedHeader />}
                <Pricing />
              </>
            }
          />

          <Route path="/admin-login" element={<AdminLogin />} />
          <Route
            path="/admin/vehicle"
            element={
              <AdminProtectedRoute>
                <AdminHeader />
                <Vehicle />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin/api-hit"
            element={
              <AdminProtectedRoute>
                <AdminHeader />
                <ApitHit />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              <AdminProtectedRoute>
                <AdminHeader />
                <AdminDashboard />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin/order"
            element={
              <AdminProtectedRoute>
                <AdminHeader />
                <Order />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin/fastag"
            element={
              <AdminProtectedRoute>
                <AdminHeader />
                <AdminFastag />
              </AdminProtectedRoute>
            }
          />

          <Route
            path="/admin/vahan"
            element={
              <AdminProtectedRoute>
                <AdminHeader />
                <AdminVahan />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin/verification"
            element={
              <AdminProtectedRoute>
                <AdminHeader />
                <Verification />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin/company"
            element={
              <AdminProtectedRoute>
                <AdminHeader />
                <Company />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/admin/driver"
            element={
              <AdminProtectedRoute>
                <AdminHeader />
                <AdminDriver />
              </AdminProtectedRoute>
            }
          />
          <Route path="/privacy-policy" element={<Privacy />} />
          <Route path="/terms-and-conditions" element={<Terms />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </div>
  );
};

export default App;
